/**
 * Thin fetch wrappers around the Question Bank HTTP surface (Module 2).
 *
 * Mirrors the envelope shape used by the auth state — see
 * `packages/web/src/state/auth/api.ts`. TanStack Query hooks are deferred to
 * Module 11.
 */

import { apiFetch } from '@/lib/api-client';

export type QuestionType =
  | 'multiple'
  | 'truefalse'
  | 'multi'
  | 'order'
  | 'short'
  | 'filltext'
  | 'fillnumber';

export type Difficulty = 'easy' | 'medium' | 'hard';

export interface QuestionOption {
  id: string;
  text: string;
  is_correct: boolean;
  sort_order: number;
}

export interface QuestionPayload {
  id: string;
  type: QuestionType;
  text: string;
  image_url: string | null;
  section_name: string | null;
  category_id: string | null;
  subcategory_id: string | null;
  benchmark_domains: string[];
  tags: Array<{ id: string; name: string }>;
  difficulty: Difficulty;
  points: number;
  explanation: string | null;
  deprecated: boolean;
  created_at: string;
  updated_at: string;
  options: QuestionOption[];
}

export interface ListPayload {
  items: QuestionPayload[];
  next_cursor: string | null;
}

export interface OptionInput {
  text: string;
  is_correct?: boolean;
  sort_order: number;
}

export interface QuestionInput {
  type: QuestionType;
  text: string;
  image_url?: string | null;
  section_name?: string | null;
  category_id?: string;
  subcategory_id?: string;
  benchmark_domains?: string[];
  tag_ids?: string[];
  difficulty?: Difficulty;
  points?: number;
  explanation?: string | null;
  options: OptionInput[];
}

export type ApiEnvelope<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      error: { code: string; message: string; field?: string };
    };

async function request<T>(
  path: string,
  init: RequestInit,
): Promise<ApiEnvelope<T>> {
  const res = await apiFetch(path, { credentials: 'same-origin', ...init });
  const text = await res.text();
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    return {
      ok: false,
      error: {
        code: 'unexpected_response',
        message:
          'Something went wrong on our side. Please try again, or refresh the page if it keeps happening.',
      },
    };
  }
}

export function listQuestions(params: {
  type?: QuestionType;
  category_id?: string;
  q?: string;
  cursor?: string;
  include_deprecated?: boolean;
}): Promise<ApiEnvelope<ListPayload>> {
  const search = new URLSearchParams();
  if (params.type) search.set('type', params.type);
  if (params.category_id) search.set('category_id', params.category_id);
  if (params.q) search.set('q', params.q);
  if (params.cursor) search.set('cursor', params.cursor);
  if (params.include_deprecated) search.set('include_deprecated', 'true');
  const qs = search.toString();
  return request<ListPayload>(
    `/api/questions${qs.length > 0 ? `?${qs}` : ''}`,
    { method: 'GET' },
  );
}

export function getQuestion(id: string): Promise<ApiEnvelope<QuestionPayload>> {
  return request<QuestionPayload>(`/api/questions/${id}`, { method: 'GET' });
}

export function createQuestion(
  input: QuestionInput,
): Promise<ApiEnvelope<QuestionPayload>> {
  return request<QuestionPayload>(`/api/questions`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function updateQuestion(
  id: string,
  input: Partial<QuestionInput>,
): Promise<ApiEnvelope<QuestionPayload>> {
  return request<QuestionPayload>(`/api/questions/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function deleteQuestion(
  id: string,
): Promise<ApiEnvelope<{ mode: 'soft' | 'hard'; id: string }>> {
  return request<{ mode: 'soft' | 'hard'; id: string }>(
    `/api/questions/${id}`,
    { method: 'DELETE' },
  );
}

// ─── Taxonomy ───────────────────────────────────────────────────────────

export interface CategoryPayload {
  id: string;
  name: string;
  color: string;
  /** Questions assigned to this category. 0 when none. */
  count: number;
}

export interface SubcategoryPayload {
  id: string;
  name: string;
}

export interface TagPayload {
  id: string;
  name: string;
  /** Questions tagged with this tag. 0 when none. */
  count: number;
}

export function listCategories(): Promise<ApiEnvelope<CategoryPayload[]>> {
  return request<CategoryPayload[]>(`/api/taxonomy/categories`, {
    method: 'GET',
  });
}

export function listSubcategories(
  categoryId: string,
): Promise<ApiEnvelope<SubcategoryPayload[]>> {
  return request<SubcategoryPayload[]>(
    `/api/taxonomy/categories/${categoryId}/subcategories`,
    { method: 'GET' },
  );
}

export function listTags(): Promise<ApiEnvelope<TagPayload[]>> {
  return request<TagPayload[]>(`/api/taxonomy/tags`, { method: 'GET' });
}

export function createTag(name: string): Promise<ApiEnvelope<TagPayload>> {
  return request<TagPayload>(`/api/taxonomy/tags`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name }),
  });
}

// ─── Image upload (Module 7 orchestration via /api/questions/:id/...) ──

export interface SignedUploadUrlPayload {
  artefact_key: string;
  upload_url: string;
  expires_at: string;
  required_headers: Record<string, string>;
}

export function requestImageUploadUrl(
  questionId: string,
  body: { filename: string; mime_type: string; size_bytes: number },
): Promise<ApiEnvelope<SignedUploadUrlPayload>> {
  return request<SignedUploadUrlPayload>(
    `/api/questions/${questionId}/image-upload-url`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export function confirmImageUpload(
  questionId: string,
  artefactKey: string,
): Promise<ApiEnvelope<{ image_url: string }>> {
  return request<{ image_url: string }>(
    `/api/questions/${questionId}/image-confirm`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ artefact_key: artefactKey }),
    },
  );
}

// ─── CSV bulk import (Story 60, issue 03-question-bank/03) ──────────────

export interface CsvImportResult {
  inserted: number;
  updated: number;
  errors: Array<{ row: number; message: string }>;
}

/**
 * POST `/api/questions/import` with a raw CSV body. The route also accepts
 * `{csv:"..."}` JSON; the raw form is preferred because it streams cleanly
 * without re-encoding multi-MB CSVs.
 */
export function importQuestionsCsv(
  csv: string,
): Promise<ApiEnvelope<CsvImportResult>> {
  return request<CsvImportResult>(`/api/questions/import`, {
    method: 'POST',
    headers: { 'content-type': 'text/csv' },
    body: csv,
  });
}
